/**
 * Reads browser signals describing the user's stated preferences, their network link
 * and their device class.
 *
 * Two rules this module must keep:
 *
 * 1. Nothing at module scope touches `window`/`navigator`, so importing it from a
 *    Preact component cannot break hypernova SSR.
 * 2. A signal that the browser does not implement reads as `null`, never `false`.
 *    Consumers distinguish "the user opted out" from "we cannot tell" — see
 *    `shouldPlayBackgroundVideo`.
 */
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const REDUCED_DATA_QUERY = "(prefers-reduced-data: reduce)";
/**
 * An unsupported media feature still yields a MediaQueryList, with `matches: false`
 * and `media === "not all"`. Only the latter tells us the browser understood us.
 */
function matchPreference(query) {
    if (typeof window === "undefined" || !window.matchMedia)
        return null;
    const mq = window.matchMedia(query);
    return mq.media === "not all" ? null : mq.matches;
}
function getConnection() {
    return typeof navigator === "undefined"
        ? undefined
        : navigator.connection;
}
export function getMediaPreferences() {
    return {
        prefersReducedMotion: matchPreference(REDUCED_MOTION_QUERY),
        prefersReducedData: matchPreference(REDUCED_DATA_QUERY),
    };
}
export function prefersReducedMotion() {
    return matchPreference(REDUCED_MOTION_QUERY) === true;
}
export function getNetworkInfo() {
    var _a, _b, _c;
    const conn = getConnection();
    return {
        saveData: (_a = conn === null || conn === void 0 ? void 0 : conn.saveData) !== null && _a !== void 0 ? _a : null,
        effectiveType: (_b = conn === null || conn === void 0 ? void 0 : conn.effectiveType) !== null && _b !== void 0 ? _b : null,
        connectionType: (_c = conn === null || conn === void 0 ? void 0 : conn.type) !== null && _c !== void 0 ? _c : null,
    };
}
export function getHardwareInfo() {
    var _a, _b;
    if (typeof navigator === "undefined") {
        return { cores: null, memoryGB: null };
    }
    return {
        cores: (_a = navigator.hardwareConcurrency) !== null && _a !== void 0 ? _a : null,
        memoryGB: (_b = navigator.deviceMemory) !== null && _b !== void 0 ? _b : null,
    };
}
/**
 * Notifies when any of the above may have changed. Returns an unsubscribe function.
 */
export function onCapabilitiesChange(callback) {
    const unsubscribers = [];
    if (typeof window !== "undefined" && window.matchMedia) {
        [REDUCED_MOTION_QUERY, REDUCED_DATA_QUERY].forEach((query) => {
            const mq = window.matchMedia(query);
            if (mq.media === "not all" || !mq.addEventListener)
                return;
            mq.addEventListener("change", callback);
            unsubscribers.push(() => mq.removeEventListener("change", callback));
        });
    }
    const conn = getConnection();
    if (conn === null || conn === void 0 ? void 0 : conn.addEventListener) {
        conn.addEventListener("change", callback);
        unsubscribers.push(() => conn.removeEventListener("change", callback));
    }
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
}
